import React, { useMemo, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { Heart, Globe, TrendingUp, X } from 'lucide-react';
import AdminUserDetailModal from '../../components/admin/AdminUserDetailModal';

// Stat Card Component
const StatCard = ({ label, value, color = 'burgundy', trend }) => {
  const colorStyles = {
    burgundy: 'border-burgundy-100',
    green: 'border-green-100',
    blue: 'border-blue-100',
    red: 'border-red-100',
  };

  return (
    <div className={`bg-white rounded-2xl border ${colorStyles[color]} p-5`}>
      <div className="flex items-start justify-between mb-2">
        <p className="text-4xl font-serif font-bold text-gray-900">{value}</p>
        {trend && (
          <span className="text-xs text-green-600 flex items-center font-medium">
            <TrendingUp className="w-3 h-3 mr-1" />
            {trend}
          </span>
        )}
      </div>
      <p className="text-sm text-gray-500 uppercase tracking-wide">{label}</p>
    </div>
  );
};

const SubscribersPage = () => {
  const { subscribers, dependants, metrics } = useApp();
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [detailUserId, setDetailUserId] = useState('');
  const dependantCountBySubscriber = dependants.reduce((acc, dependant) => {
    const subscriberId = dependant.subscriberId || dependant.subscriber_id;
    if (subscriberId) acc[subscriberId] = (acc[subscriberId] || 0) + 1;
    return acc;
  }, {});
  const activeSubscribers = subscribers.filter(subscriber => {
    const status = String(subscriber.subscriptionStatus || subscriber.subscription_status || '').toLowerCase();
    return !status || status === 'active';
  });
  const walletBalances = subscribers
    .map(subscriber => Number(subscriber.walletBalance ?? subscriber.wallet_balance ?? 0))
    .filter(value => Number.isFinite(value));
  const averageWallet = walletBalances.length
    ? walletBalances.reduce((sum, value) => sum + value, 0) / walletBalances.length
    : 0;
  const recentSubscribers = [...subscribers].sort((a, b) => (
    new Date(b.createdAt || b.created_at || b.joinedDate || 0) - new Date(a.createdAt || a.created_at || a.joinedDate || 0)
  ));

  const getName = (subscriber) => (
    [subscriber.firstName || subscriber.first_name, subscriber.lastName || subscriber.last_name].filter(Boolean).join(' ') || subscriber.email || 'Subscriber'
  );
  const getInitials = (subscriber) => {
    const name = getName(subscriber).trim();
    return name.split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase() || 'S';
  };
  const formatCurrency = (value, currency = 'USD') => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
  const selectedDependants = useMemo(() => {
    if (!selectedSubscriber) return [];
    const ids = new Set([selectedSubscriber.id, selectedSubscriber._id].filter(Boolean));
    return dependants.filter(dependant => ids.has(dependant.subscriberId || dependant.subscriber_id));
  }, [dependants, selectedSubscriber]);

  return (
    <div className="min-h-screen bg-cream" data-testid="subscribers-page">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div>
            <h1 className="font-serif text-2xl font-bold text-gray-900">Subscribers</h1>
            <p className="text-sm text-gray-500 mt-1">Manage subscriber accounts and subscriptions</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          <StatCard label="Total Subscribers" value={subscribers.length} color="burgundy" trend={metrics?.monthlyGrowthRate ? `+${metrics.monthlyGrowthRate}%` : null} />
          <StatCard label="Active Subscribers" value={activeSubscribers.length} color="green" />
          <StatCard label="Dependants" value={dependants.length} color="blue" />
          <StatCard label="Avg. Wallet" value={formatCurrency(averageWallet)} color="green" />
        </div>

        {/* Subscribers List */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="font-serif text-lg font-semibold text-gray-900">All Subscribers</h2>
              <p className="text-sm text-gray-500">Showing recent subscribers</p>
            </div>
            <button className="px-4 py-2 bg-burgundy-600 text-[#FAFAF7] text-sm font-medium rounded-full hover:bg-burgundy-700 transition-colors">
              Export CSV
            </button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscriber</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dependants</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Wallet</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {recentSubscribers.map((sub) => (
                  <tr key={sub.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-burgundy-100 flex items-center justify-center text-burgundy-600 font-bold">
                          {getInitials(sub)}
                        </div>
                        <div>
                          <button
                            type="button"
                            onClick={() => {
                              const userId = sub.userId || sub.user_id;
                              if (userId) setDetailUserId(userId);
                            }}
                            disabled={!(sub.userId || sub.user_id)}
                            className="text-left font-medium text-gray-900 hover:text-[#9F1D35] disabled:cursor-default disabled:hover:text-gray-900"
                          >
                            {getName(sub)}
                          </button>
                          <p className="text-sm text-gray-500">{sub.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 text-gray-600">
                        <Globe className="w-4 h-4 text-gray-400" />
                        {[sub.city, sub.country].filter(Boolean).join(', ') || 'Not provided'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        sub.plan === 'Prestige' ? 'bg-burgundy-100 text-burgundy-700' :
                        sub.plan === 'Comfort' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {sub.plan || 'Unassigned'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 text-gray-600">
                        <Heart className="w-4 h-4 text-red-400" />
                        {dependantCountBySubscriber[sub.id] || dependantCountBySubscriber[sub._id] || 0}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-medium text-green-600">
                        {formatCurrency(sub.walletBalance ?? sub.wallet_balance, sub.walletCurrency || sub.wallet_currency || 'USD')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-500">
                      {sub.createdAt || sub.created_at || sub.joinedDate
                        ? new Date(sub.createdAt || sub.created_at || sub.joinedDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                        : 'Unknown'}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        type="button"
                        onClick={() => setSelectedSubscriber(sub)}
                        className="text-burgundy-600 hover:text-burgundy-700 font-medium text-sm"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {recentSubscribers.length === 0 && (
            <div className="p-12 text-center text-sm text-gray-500">
              No subscribers found.
            </div>
          )}
        </div>
      </div>

      {selectedSubscriber && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-6">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#9F1D35]">Subscriber record</p>
                <h2 className="mt-2 font-serif text-3xl font-semibold text-[#1A1A1A]">{getName(selectedSubscriber)}</h2>
                <p className="mt-1 text-sm text-[#5A5A5A]">{selectedSubscriber.email || 'No email'} · {selectedSubscriber.phone || 'No phone'}</p>
              </div>
              <button type="button" onClick={() => setSelectedSubscriber(null)} className="rounded-full border border-[#D8D5D0] p-2 text-[#5A5A5A]" aria-label="Close subscriber details">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid gap-4 md:grid-cols-4">
              <div className="rounded-2xl border border-[#E8E6E3] bg-white p-4">
                <p className="text-xs uppercase tracking-[0.12em] text-[#5A5A5A]">Plan</p>
                <p className="mt-2 font-semibold text-[#1A1A1A]">{selectedSubscriber.plan || 'Unassigned'}</p>
              </div>
              <div className="rounded-2xl border border-[#E8E6E3] bg-white p-4">
                <p className="text-xs uppercase tracking-[0.12em] text-[#5A5A5A]">Dependants</p>
                <p className="mt-2 font-semibold text-[#1A1A1A]">{selectedDependants.length}</p>
              </div>
              <div className="rounded-2xl border border-[#E8E6E3] bg-white p-4">
                <p className="text-xs uppercase tracking-[0.12em] text-[#5A5A5A]">Wallet</p>
                <p className="mt-2 font-semibold text-[#1A1A1A]">{formatCurrency(selectedSubscriber.walletBalance ?? selectedSubscriber.wallet_balance, selectedSubscriber.walletCurrency || selectedSubscriber.wallet_currency || 'USD')}</p>
              </div>
              <div className="rounded-2xl border border-[#E8E6E3] bg-white p-4">
                <p className="text-xs uppercase tracking-[0.12em] text-[#5A5A5A]">Status</p>
                <p className="mt-2 font-semibold text-[#1A1A1A]">{selectedSubscriber.subscriptionStatus || selectedSubscriber.subscription_status || 'Active'}</p>
              </div>
            </div>
            <div className="mt-5 rounded-2xl border border-[#E8E6E3] bg-white p-5">
              <h3 className="font-serif text-xl font-semibold text-[#1A1A1A]">Dependants</h3>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {selectedDependants.map(dependant => (
                  <div key={dependant.id || dependant._id} className="rounded-xl border border-[#E8E6E3] p-4">
                    <p className="font-medium text-[#1A1A1A]">{dependant.name || [dependant.firstName, dependant.lastName].filter(Boolean).join(' ') || 'Dependant'}</p>
                    <p className="mt-1 text-sm text-[#5A5A5A]">{dependant.plan || selectedSubscriber.plan || 'No plan'} · {dependant.status || 'Active'}</p>
                  </div>
                ))}
                {selectedDependants.length === 0 && <p className="text-sm text-[#5A5A5A]">No dependants linked yet.</p>}
              </div>
            </div>
          </div>
        </div>
      )}
      <AdminUserDetailModal
        userId={detailUserId}
        open={Boolean(detailUserId)}
        onOpenChange={(open) => !open && setDetailUserId('')}
      />
    </div>
  );
};

export default SubscribersPage;
